import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, CircularProgress } from '@mui/material';
import { useEmployees, useEmployee } from '@/api/employees';

/**
 * Reusable employee autosuggest.
 *
 * Drop-in replacement for the legacy `<TextField select native>` employee
 * dropdowns scattered around the app. Built on MUI Autocomplete with a
 * 250ms debounced server-side search through `useEmployees({ search })`
 * so it stays responsive on tenants with thousands of employees.
 *
 * Why server-side: fetching the entire employee list to filter client-
 * side blows the wire for any non-trivial tenant. The list endpoint
 * already supports `search` against emp_id + first/last name.
 *
 * Edge cases handled:
 *  - When `value` is set but the matching employee isn't in the current
 *    search-result page (e.g. URL-restored filter on a 5000-emp tenant)
 *    we additionally fetch the single record via `useEmployee(value)`
 *    so the input renders the right name instead of just the UUID.
 *  - Empty / cleared selection → onChange(null). Callers that need ''
 *    semantics should treat null === ''.
 */
export type EmployeePickerProps = {
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  sx?: object;
  /** Limit the picker to a single entity (defaults to all). */
  entity_id?: string;
  /** Status filter; defaults to no filter so REJOIN / RESIGNED show too. */
  status?: string;
  disabled?: boolean;
  /** Width override; default 260. */
  width?: number | string;
  /** When true, the field shows `error` styling. */
  error?: boolean;
  helperText?: string;
};

export function EmployeePicker({
  value, onChange,
  label = 'Employee',
  placeholder = 'Search by name or code…',
  size = 'small',
  sx,
  entity_id,
  status,
  disabled,
  width = 260,
  error,
  helperText,
}: EmployeePickerProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // 250 ms debounce — matches the "feels live" threshold without
  // hammering the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const list = useEmployees({
    search: debouncedQuery || undefined,
    entity_id: entity_id || undefined,
    status: status || undefined,
    page: 1,
    page_size: 50,
  });

  // Fallback: if `value` is set but not in the current search results,
  // fetch the single record so the field doesn't display a bare UUID.
  const fallback = useEmployee(
    value && !(list.data ?? []).some((e) => e.id === value) ? value : undefined,
  );

  const options = useMemo(() => {
    const base = list.data ?? [];
    if (fallback.data && !base.some((e) => e.id === fallback.data!.id)) {
      return [fallback.data, ...base];
    }
    return base;
  }, [list.data, fallback.data]);

  const selected = useMemo(
    () => options.find((e) => e.id === value) ?? null,
    [options, value],
  );

  return (
    <Autocomplete
      size={size}
      sx={{ width, ...(sx || {}) }}
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_, opt) => {
        onChange(opt?.id ?? null);
        // Clear the search text on selection so the next open starts fresh;
        // MUI then displays the selected employee's label (via getOptionLabel).
        setQuery('');
      }}
      // NOTE: do NOT control `inputValue` here. Fully controlling it froze the
      // field to the typed search text and hid the selected employee's name
      // after picking. We only capture the typed text (reason === 'input') to
      // drive the server-side search; MUI manages what the input displays, so
      // the selected option's label shows correctly once chosen.
      onInputChange={(_, v, reason) => {
        if (reason === 'input') setQuery(v);
        if (reason === 'clear') setQuery('');
      }}
      getOptionLabel={(opt) => {
        const fullName = [opt.first_name, opt.last_name].filter(Boolean).join(' ');
        return opt.emp_id ? `${fullName} (${opt.emp_id})` : fullName;
      }}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      filterOptions={(opts) => opts}  // server-side: trust the result
      loading={list.isFetching || fallback.isFetching}
      noOptionsText={
        debouncedQuery ? 'No employees match — try a different search.' : 'Start typing to search…'
      }
      renderOption={(props, opt) => (
        <Box component="li" {...props} key={opt.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontWeight: 600, fontSize: 13 }}>
              {[opt.first_name, opt.last_name].filter(Boolean).join(' ')}
            </Box>
            <Box sx={{ fontSize: 11, color: 'text.secondary', fontFamily: 'ui-monospace, monospace' }}>
              {opt.emp_id}
              {opt.designation ? ` · ${opt.designation}` : ''}
            </Box>
          </Box>
          {opt.status && opt.status !== 'ACTIVE' && (
            <Box sx={{ fontSize: 10, color: 'text.secondary' }}>{opt.status}</Box>
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(list.isFetching || fallback.isFetching) && <CircularProgress size={14} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
